import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Paper,
  List,
  ListItem,
  Button,
  CircularProgress,
  Avatar,
  useTheme,
} from '@mui/material';
import { ContentType, ChatMessage, ClassId } from '../types';
import { generateText, generateTextSession, generateTextAccess } from '../services/api';
import ReactMarkdown from 'react-markdown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface TextGenerationTabProps {
  contentType: ContentType;
  classId?: ClassId;
  sessionId?: string;
  userId?: number;
  accessId?: string;
}

const contentDescriptions: Record<ContentType, string> = {
  'Normal': '일반적인 AI 어시스턴트와 대화해보세요.',
  'AI 크리에이터': 'AI 크리에이터와 함께 창의적인 콘텐츠를 만들어보세요.',
  'AI 윤리 & 사회변화': 'AI가 가져올 사회 변화와 윤리적 문제에 대해 토론해보세요.',
  '차세대 기업가 정신': '미래 기업가로서의 역량을 키워보세요.',
  '미래 사회 탐험가': '다가올 미래 사회의 모습을 탐험해보세요.',
  '미스터리 코드': '코딩의 세계를 탐험하고 문제를 해결해보세요.',
  'AI 트랜드 익스플로러': 'AI 트렌드를 탐험하고 분석해보세요.',
};

const contentImages: Record<ContentType, string | undefined> = {
  'Normal': undefined,
  'AI 크리에이터': '/assets/AIcreator_LISA.jpg',
  'AI 윤리 & 사회변화': '/assets/YUN.jpg',
  '차세대 기업가 정신': '/assets/CHANG.jpg',
  '미래 사회 탐험가': '/assets/LUNA.jpg',
  '미스터리 코드': '/assets/REX.jpg',
  'AI 트랜드 익스플로러': '/assets/AIEXPLORER.jpg',
};

const TextGenerationTab: React.FC<TextGenerationTabProps> = ({ contentType, classId, sessionId, userId, accessId }) => {
  const theme = useTheme();
  // 메시지 상태 초기화 및 로컬 스토리지에서 복원
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const savedMessages = localStorage.getItem(`messages_${contentType}`);
    return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const [notes, setNotes] = useState(() => {
    const savedNotes = localStorage.getItem(`notes_${contentType}`);
    return savedNotes || '';
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 메시지가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem(`messages_${contentType}`, JSON.stringify(messages));
  }, [messages, contentType]);

  // 노트가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem(`notes_${contentType}`, notes);
  }, [notes, contentType]);

  // contentType이 변경될 때 해당 타입의 저장된 데이터 불러오기
  useEffect(() => {
    const savedMessages = localStorage.getItem(`messages_${contentType}`);
    const savedNotes = localStorage.getItem(`notes_${contentType}`);

    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
    if (savedNotes) {
      setNotes(savedNotes);
    }
  }, [contentType]);

  // 자동 스크롤 처리
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  const handleInputFocus = useCallback(() => {
    setTimeout(scrollToBottom, 100);
  }, [scrollToBottom]);

  // API 호출 처리 (handleSubmit 함수):
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      let response: string;
      
      // API 호출 우선순위: accessId > sessionId > classId
      if (accessId) {
        response = await generateTextAccess(input, accessId, contentType);
      } else if (sessionId && userId) {
        response = await generateTextSession(input, sessionId, userId, contentType);
      } else if (classId) {
        response = await generateText(input, classId, contentType);
      } else {
        throw new Error('No valid API configuration found');
      }
      
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating text:', error);
    } finally {
      setIsLoading(false);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [input, isLoading, classId, contentType, accessId, sessionId, userId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        inputRef.current?.focus();
      }
    }
  }, [isLoading, messages]);

  // 입력 필드 변경 핸들러
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  }, []);

  return (
    <Box
      role="main"
      aria-label={`${contentType} 대화`}
      sx={{
        height: 'calc(100vh - 180px)', // Navbar, Container padding 등을 고려한 높이
        maxHeight: 'calc(100vh - 180px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* 헤더 영역 */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 }, py: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem', lg: '1.5rem' },
            color: 'white',
            fontWeight: 600,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
          }}
        >
          {contentDescriptions[contentType]}
        </Typography>
      </Box>

      {/* 메인 채팅 영역 */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 1.5, sm: 2, md: 2.5, lg: 3 }}
          sx={{
            height: '100%',
            maxHeight: '100%',
            m: 0,
            overflow: 'hidden',
            '& > .MuiGrid-item': {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
            }
          }}
        >
          {contentImages[contentType] && (
            <Grid item xs={12} sm={12} md={3} lg={2.5} xl={2}>
              <Paper
                elevation={0}
                sx={{
                  height: { xs: '200px', sm: '250px', md: '100%' },
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px',
                  background: 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  boxShadow: 'none',
                }}
              >
                <Box
                  component="img"
                  src={contentImages[contentType]}
                  alt={`${contentType} 캐릭터`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '16px',
                  }}
                />
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} sm={12} md={contentImages[contentType] ? 9 : 12} lg={contentImages[contentType] ? 9.5 : 12} xl={contentImages[contentType] ? 10 : 12}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                maxHeight: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                background: 'transparent',
                backdropFilter: 'none',
                border: 'none',
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: '100%',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.5)',
                    },
                  },
                }}
              >
                <List sx={{ 
                  p: 0, 
                  flex: 1,
                  overflow: 'visible',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {messages.map((message, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        px: 0,
                        py: { xs: 0.5, sm: 0.75, md: 1 },
                        mb: { xs: 1, sm: 1.5, md: 2 },
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          maxWidth: { xs: '85%', sm: '80%', md: '75%', lg: '70%' },
                          flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                        }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 28, sm: 30, md: 32 },
                            height: { xs: 28, sm: 30, md: 32 },
                            mr: message.role === 'user' ? 0 : { xs: 1, sm: 1.25, md: 1.5 },
                            ml: message.role === 'user' ? { xs: 1, sm: 1.25, md: 1.5 } : 0,
                            bgcolor: message.role === 'user' ? 'rgba(59, 130, 246, 0.8)' : 'rgba(16, 185, 129, 0.8)',
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                          }}
                        >
                          {message.role === 'user' ? 'U' : 'AI'}
                        </Avatar>
                        <Box
                          sx={{
                            p: { xs: 1.5, sm: 2, md: 2.5 },
                            borderRadius: '12px',
                            background: message.role === 'user' 
                              ? 'rgba(59, 130, 246, 0.1)' 
                              : 'rgba(16, 185, 129, 0.1)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                            lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                            '& p': {
                              margin: 0,
                              marginBottom: { xs: '6px', sm: '8px' },
                              '&:last-child': {
                                marginBottom: 0,
                              },
                            },
                            '& pre': {
                              background: 'rgba(0, 0, 0, 0.3)',
                              padding: { xs: '8px', sm: '10px', md: '12px' },
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              overflow: 'auto',
                              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                            },
                            '& code': {
                              background: 'rgba(0, 0, 0, 0.3)',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                            },
                          }}
                        >
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>

                {isLoading && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: { xs: 1.5, sm: 2 },
                      mt: 'auto',
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: 'white' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        ml: { xs: 0.75, sm: 1 }, 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' }
                      }}
                    >
                      응답 생성 중...
                    </Typography>
                  </Box>
                )}
                <div ref={messagesEndRef} style={{ height: '1px', flexShrink: 0 }} />
              </Box>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                  pt: 0,
                  borderTop: 'none',
                  background: 'transparent',
                  backdropFilter: 'none',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 } }}>
                  <TextField
                    ref={inputRef}
                    fullWidth
                    multiline
                    maxRows={3}
                    value={input}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder="메시지를 입력하세요... (Enter: 전송, Shift+Enter: 줄바꿈)"
                    variant="outlined"
                    disabled={isLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'white',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                        fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                        },
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading || !input.trim()}
                    sx={{
                      minWidth: { xs: 48, sm: 56, md: 64 },
                      height: { xs: 48, sm: 52, md: 56 },
                      borderRadius: '12px',
                      background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' } }} />
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TextGenerationTab; 